import { useCallback } from 'react';
import { useDynamicExperienceContext } from '../providers/DynamicExperienceProvider';
import type { EventContext } from '../utils/createEventingPayloadsDE';
import {
  createFormStartedPayload,
  createFormContinuedPayload,
  createFormEventsPayload,
  createFormViewedPayload,
  createFieldEventsPayload,
  createProductLoadedPayload,
  createProductViewedPayload,
  createElementLoadedPayload,
  createStepElementLoadedPayload,
  createStepElementViewedPayload,
  createProductClickedPayload,
  createModalPayload,
  createModalClosedPayload,
  createElementClickedPayload,
  createHostElementClickedPayload,
  createHostElementLoadedPayload,
  createHostElementViewedPayload,
  createHostFieldEventsPayload,
  createHostElementEventContext,
  type HostElementEventContext,
} from '../utils/createEventingPayloadsDE';
import tagularEvent from '../utils/tagularEvent';
import {
  isDefaultOnHostPolicy,
  type TrackingHostPolicy,
} from '../providers/TrackingHostContext';

export { createHostElementEventContext, type HostElementEventContext };

/**
 * Flow tracking events: builds payloads via createEventingPayloadsDE and sends via tagular
 */
export const useTrackingEventTriggers = (
  eventContext: EventContext | undefined,
  policy: TrackingHostPolicy = 'flow-defaults',
) => {
  const usesHostElementPayloads =
    policy === 'explicit-node-enable' || isDefaultOnHostPolicy(policy);
  const triggerFormStartedEvent = useCallback(
    (overrideEventContext?: EventContext) => {
      const ctx = (overrideEventContext ?? eventContext) as EventContext;
      const { name, payload } = createFormStartedPayload(ctx);
      return tagularEvent(name, payload);
    },
    [eventContext],
  );

  const triggerFormContinuedEvent = useCallback(
    (overrideEventContext?: EventContext) => {
      const ctx = (overrideEventContext ?? eventContext) as EventContext;
      const { name, payload } = createFormContinuedPayload(ctx);
      return tagularEvent(name, payload);
    },
    [eventContext],
  );

  const triggerFormEvents = useCallback(
    (eventName: string, overrideEventContext: EventContext) => {
      const { name, payload } = createFormEventsPayload(
        overrideEventContext,
        eventName,
      );
      return tagularEvent(name, payload);
    },
    [],
  );

  const triggerFieldEvents = useCallback(
    (eventName: string, overrideEventContext: EventContext) => {
      const hostFormIdentity =
        isDefaultOnHostPolicy(policy) && eventContext
          ? {
              formName: eventContext.formName,
              formType: eventContext.formType,
              ...(Object.prototype.hasOwnProperty.call(eventContext, 'formId')
                ? { formId: eventContext.formId }
                : {}),
            }
          : undefined;
      const ctx = (
        usesHostElementPayloads
          ? {
              ...eventContext,
              ...overrideEventContext,
              ...(hostFormIdentity ?? {}),
            }
          : overrideEventContext
      ) as EventContext;
      const { name, payload } = usesHostElementPayloads
        ? createHostFieldEventsPayload(ctx, eventName)
        : createFieldEventsPayload(ctx, eventName);
      return tagularEvent(name, payload);
    },
    [eventContext, usesHostElementPayloads, policy],
  );

  const triggerProductLoadedEvent = useCallback(
    (overrideEventContext?: Partial<EventContext>) => {
      const ctx = { ...eventContext, ...overrideEventContext } as EventContext;
      const { name, payload } = createProductLoadedPayload(ctx);
      return tagularEvent(name, payload);
    },
    [eventContext],
  );

  const triggerStepElementLoadedEvent = useCallback(() => {
    const { name, payload } = createStepElementLoadedPayload(
      eventContext as EventContext,
    );
    return tagularEvent(name, payload);
  }, [eventContext]);

  const triggerStepElementViewedEvent = useCallback(() => {
    const { name, payload } = createStepElementViewedPayload(
      eventContext as EventContext,
    );
    return tagularEvent(name, payload);
  }, [eventContext]);

  const triggerElementLoadedEvent = useCallback(
    (overrideEventContext?: Partial<EventContext>) => {
      const ctx = { ...eventContext, ...overrideEventContext } as EventContext;
      const { name, payload } = usesHostElementPayloads
        ? createHostElementLoadedPayload(
            ctx as unknown as Readonly<Record<string, unknown>>,
          )
        : createElementLoadedPayload(ctx);
      return tagularEvent(name, payload);
    },
    [eventContext, usesHostElementPayloads],
  );

  const triggerProductClickedEvent = useCallback(
    (overrideEventContext: EventContext) => {
      const { name, payload } =
        createProductClickedPayload(overrideEventContext);
      return tagularEvent(name, payload);
    },
    [],
  );

  /**
   * Emit `ProductViewed` for the DSL render path
   * (EDUN-10692 review-fix Critical #2).
   *
   * The existing `createProductViewedPayload` builder already accepts a
   * full `EventContext` and emits the product-shaped wire payload (see
   * the entry-point `ProductViewed` from
   * `useDynamicExperienceEventing`). This trigger merges an
   * `overrideEventContext` partial over the inherited `eventContext`
   * so `Trackable.fireViewEvent`'s `kind: 'product'` branch can
   * thread StepCorrelation customDimensions + `location` / `ctaText`
   * / `outboundUrl` / `actionOutcome` (the latter three flow through
   * `customDimensions` per the admin contract; see plan §3.3) onto
   * the wire without a new payload builder.
   *
   * Same shape as `triggerProductLoadedEvent` — the standard path
   * established the merge pattern; this trigger mirrors it for the
   * DSL render path.
   */
  const triggerProductViewedEvent = useCallback(
    (overrideEventContext?: Partial<EventContext>) => {
      const ctx = { ...eventContext, ...overrideEventContext } as EventContext;
      const { name, payload } = createProductViewedPayload(ctx);
      return tagularEvent(name, payload);
    },
    [eventContext],
  );

  /**
   * Emit `FormViewed` for the DSL render path.
   *
   * Mirrors `triggerProductLoadedEvent`: builds the payload via
   * `createFormViewedPayload` by merging an `overrideEventContext`
   * partial on top of the inherited `eventContext`. Surfaced on
   * `TrackingTriggers` so `useEntryPointEventFiring` (and any future
   * DSL consumer that needs a direct dispatch) has a single callable.
   *
   * The builder produces the spec-compliant wire shape
   * (`formContext.formId = dexp-<flowId>`, `correlationId =
   * formViewedCorrelationId`, `sourceCorrelationId =
   * elementLoadedCorrelationId`).
   */
  const triggerFormViewedEvent = useCallback(
    (overrideEventContext?: Partial<EventContext>) => {
      const ctx = { ...eventContext, ...overrideEventContext } as EventContext;
      const { name, payload } = createFormViewedPayload(ctx);
      return tagularEvent(name, payload);
    },
    [eventContext],
  );

  const triggerModalElementViewedEvent = useCallback(() => {
    const { name, payload } = createModalPayload(
      'ElementViewed',
      eventContext as EventContext,
    );
    return tagularEvent(name, payload);
  }, [eventContext]);

  const triggerModalElementClickedEvent = useCallback(() => {
    const { name, payload } = createModalPayload(
      'ElementClicked',
      eventContext as EventContext,
    );
    return tagularEvent(name, payload);
  }, [eventContext]);

  const triggerModalElementClosedEvent = useCallback(() => {
    const { name, payload } = createModalClosedPayload(
      'ElementClicked',
      eventContext as EventContext,
    );
    return tagularEvent(name, payload);
  }, [eventContext]);

  const triggerElementClickedEvent = useCallback(
    (overrideEventContext?: Partial<EventContext>) => {
      const mergedContext = { ...eventContext, ...overrideEventContext };
      if (usesHostElementPayloads) {
        const { name, payload } = createHostElementClickedPayload(
          mergedContext as Readonly<Record<string, unknown>>,
        );
        return tagularEvent(name, payload);
      }

      const ctx = mergedContext as EventContext;
      const { name, payload } = createElementClickedPayload(ctx);
      return tagularEvent(name, payload);
    },
    [eventContext, usesHostElementPayloads],
  );

  const triggerElementViewedEvent = useCallback(
    (overrideEventContext?: Partial<EventContext>) => {
      const mergedContext = { ...eventContext, ...overrideEventContext };
      if (usesHostElementPayloads) {
        const { name, payload } = createHostElementViewedPayload(
          mergedContext as Readonly<Record<string, unknown>>,
        );
        return tagularEvent(name, payload);
      }

      const ctx = mergedContext as EventContext;
      const { name, payload } = createStepElementViewedPayload(ctx);
      return tagularEvent(name, payload);
    },
    [eventContext, usesHostElementPayloads],
  );

  return {
    triggerProductLoadedEvent,
    triggerProductViewedEvent,
    triggerStepElementLoadedEvent,
    triggerStepElementViewedEvent,
    triggerFieldEvents,
    triggerFormStartedEvent,
    triggerFormContinuedEvent,
    triggerFormViewedEvent,
    triggerFormEvents,
    triggerElementLoadedEvent,
    triggerProductClickedEvent,
    triggerModalElementViewedEvent,
    triggerModalElementClickedEvent,
    triggerModalElementClosedEvent,
    triggerElementClickedEvent,
    triggerElementViewedEvent,
    eventContext,
  };
};

const useFlowTrackingEvents = () => {
  const { eventContext } = useDynamicExperienceContext();
  return {
    ...useTrackingEventTriggers(eventContext as EventContext | undefined),
    eventContext: eventContext as EventContext,
  };
};

export default useFlowTrackingEvents;
